import React, { useState } from "react";
import { Box, Typography, Paper, Chip, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Audiotrack as AudioIcon,
  Description as DocumentIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon
} from '@mui/icons-material';

// Styled components
const MediaContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9edef',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  }
}));

const ErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  backgroundColor: '#fff3cd',
  border: '1px solid #ffeaa7',
  borderRadius: '12px',
  color: '#856404',
  minHeight: '120px',
}));

const DocumentPreview = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid #e9edef',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    transform: 'translateY(-1px)',
  }
}));

export default function MediaMessage(props) {
  const [imageError, setImageError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [documentError, setDocumentError] = useState(false);

  const getMediaUrl = (mediaType) => {
    if (!props.isSender) {
      return `${process.env.REACT_APP_BACKEND_API_MEDIA}/${props.media[mediaType].id}.${props.media[mediaType].mime_type.split('/')[1]}`;
    } else {
      return props.media.text.url;
    }
  };

  const getDocumentUrl = () => {
    if (!props.isSender) {
      return `${process.env.REACT_APP_BACKEND_API_MEDIA}/${props.media.document.id}.${props.media.document.filename.split('.').pop().toLowerCase()}`;
    } else {
      return props.media.text.url;
    }
  };

  const getFileName = () => {
    if (!props.isSender) {
      return props.media.document.filename;
    } else {
      return props.media.text.caption || 'Document';
    }
  };

  const getFileExtension = () => {
    if (!props.isSender) {
      return props.media.document.filename.split('.').pop().toLowerCase();
    } else {
      return props.media.text.url.split('.').pop().toLowerCase();
    }
  };

  const getFileIcon = (extension) => {
    const iconMap = {
      'pdf': <DocumentIcon sx={{ color: '#dc3545' }} />,
      'doc': <DocumentIcon sx={{ color: '#0d6efd' }} />,
      'docx': <DocumentIcon sx={{ color: '#0d6efd' }} />,
      'xls': <DocumentIcon sx={{ color: '#198754' }} />,
      'xlsx': <DocumentIcon sx={{ color: '#198754' }} />,
      'ppt': <DocumentIcon sx={{ color: '#fd7e14' }} />,
      'pptx': <DocumentIcon sx={{ color: '#fd7e14' }} />,
      'txt': <DocumentIcon sx={{ color: '#6c757d' }} />,
      'zip': <DocumentIcon sx={{ color: '#6f42c1' }} />,
      'rar': <DocumentIcon sx={{ color: '#6f42c1' }} />
    };
    return iconMap[extension] || <DocumentIcon sx={{ color: '#6c757d' }} />;
  };

  const renderErrorState = (mediaType) => (
    <ErrorContainer>
      <ErrorIcon sx={{ fontSize: 32, marginBottom: 1, color: '#dc3545' }} />
      <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: 0.5 }}>
        {mediaType === 'image' && 'Image not found'}
        {mediaType === 'video' && 'Video not found'}
        {mediaType === 'audio' && 'Audio not found'}
        {mediaType === 'document' && 'Document not found'}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: 'center', opacity: 0.7 }}>
        The file may have been deleted or is no longer available
      </Typography>
    </ErrorContainer>
  );

  const mediaType = () => {
    if (props.media.type === "image") {
      if (imageError) {
        return renderErrorState('image');
      }
      
      return (
        <MediaContainer>
          <img
            src={getMediaUrl('image')}
            alt={!props.isSender ? props.media.image.id : props.media.text.url}
            style={{ 
              width: '100%', 
              height: 'auto', 
              maxHeight: '300px',
              objectFit: 'cover',
              borderRadius: '12px'
            }}
            onError={() => setImageError(true)}
          />
          {(!props.isSender ? props.media.image.caption : props.media.text.caption) && (
            <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.05)' }}>
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {!props.isSender ? props.media.image.caption : props.media.text.caption}
              </Typography>
            </Box>
          )}
        </MediaContainer>
      );
    }
    else if (props.media.type === "sticker") {
      if (imageError) {
        return renderErrorState('image');
      }
      
      return (
        <MediaContainer sx={{ maxWidth: '120px' }}>
          <img
            src={getMediaUrl('sticker')}
            alt={!props.isSender ? props.media.sticker.id : props.media.text.caption}
            style={{ 
              width: '100px', 
              height: '100px',
              objectFit: 'contain'
            }}
            onError={() => setImageError(true)}
          />
        </MediaContainer>
      );
    }
    else if (props.media.type === "video") {
      if (videoError) {
        return renderErrorState('video');
      }
      
      return (
        <MediaContainer>
          <video 
            controls 
            style={{ 
              width: '100%', 
              maxHeight: '300px',
              borderRadius: '12px'
            }}
            onError={() => setVideoError(true)}
          >
            <source src={getMediaUrl('video')} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          {(!props.isSender ? props.media.video.caption : props.media.text.caption) && (
            <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.05)' }}>
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {!props.isSender ? props.media.video.caption : props.media.text.caption}
              </Typography>
            </Box>
          )}
        </MediaContainer>
      );
    } 
    else if (props.media.type === "audio") {
      if (audioError) {
        return renderErrorState('audio');
      }
      
      return (
        <MediaContainer>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <AudioIcon sx={{ fontSize: 32, color: '#008069' }} />
            <audio 
              controls 
              style={{ flex: 1 }}
              onError={() => setAudioError(true)}
            >
              <source src={getMediaUrl('audio')} type="audio/ogg" />
              Your browser does not support the audio tag.
            </audio>
          </Box>
        </MediaContainer>
      );
    } 
    else if (props.media.type === "document" || props.media.type === "file") {
      if (documentError) {
        return renderErrorState('document');
      }
      
      const fileExtension = getFileExtension();
      const fileName = getFileName();
      
      return (
        <DocumentPreview
          onClick={() => {
            try {
              window.open(getDocumentUrl(), '_blank');
            } catch (error) {
              setDocumentError(true);
            }
          }}
          onError={() => setDocumentError(true)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}>
            {getFileIcon(fileExtension)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fileName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#667781', textTransform: 'uppercase' }}>
                {fileExtension} file
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" sx={{ color: '#008069' }}>
            <DownloadIcon />
          </IconButton>
        </DocumentPreview>
      );
    }
  };

  return mediaType();
}